import { Amplify } from 'aws-amplify'
import {
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  resetPassword,
  confirmResetPassword,
  SignInInput,
} from 'aws-amplify/auth'
import { jwtDecode } from 'jwt-decode'
import { env } from '../utils/env'
import { PortalIsolation } from '../utils/portalIsolation'

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: env.AWS_USER_POOL_ID,
      userPoolClientId: env.AWS_USER_POOL_WEB_CLIENT_ID,
    },
  },
})

// Admin portal identifier for cross-tab detection
const ADMIN_PORTAL_ID = 'admin-portal-session'

export interface CognitoUser {
  id: string
  username: string
  email: string
  email_verified: boolean
  given_name?: string
  family_name?: string
  name?: string
  groups?: string[]
}

export interface AuthTokens {
  accessToken: string
  idToken: string
}

export class CognitoAuthService {
  /**
   * Sign in with username and password
   */
  static async signIn(
    username: string,
    password: string,
    rememberMe: boolean = false
  ): Promise<{
    user: CognitoUser
    tokens: AuthTokens
  }> {
    try {
      // First, clear any existing authentication state
      try {
        await signOut()
      } catch (error) {
        // Ignore signOut errors - user might not be signed in
      }

      // Clear local storage for admin portal
      localStorage.removeItem('admin-auth-tokens')
      localStorage.removeItem('admin-auth-user')
      sessionStorage.removeItem('admin-auth-initialized')

      const signInInput: SignInInput = {
        username,
        password,
      }

      const { isSignedIn, nextStep } = await signIn(signInInput)

      if (!isSignedIn) {
        throw new Error(`Sign in incomplete: ${nextStep?.signInStep}`)
      }

      // Get user info and tokens
      const [user, session] = await Promise.all([
        getCurrentUser(),
        fetchAuthSession(),
      ])

      if (!session.tokens?.accessToken || !session.tokens?.idToken) {
        throw new Error('Failed to retrieve authentication tokens')
      }

      // Decode the ID token to get user information
      const payload = jwtDecode(session.tokens.idToken.toString()) as any

      const cognitoUser: CognitoUser = {
        id: payload.sub,
        username: user.username,
        email: payload.email,
        email_verified: payload.email_verified,
        given_name: payload.given_name,
        family_name: payload.family_name,
        name: payload.name,
        groups: payload['cognito:groups'] || [],
      }

      // Check if user has access to the application (allow specific users and group members)
      const userGroups = cognitoUser.groups || []
      const hasGroupAccess = userGroups.some(group =>
        ['data-admin', 'admin', 'finance-advisor', 'finance'].includes(group)
      )

      // Allow specific usernames even without group membership
      const hasUsernameAccess = cognitoUser.username === 'test-compliance-user'

      if (!hasGroupAccess && !hasUsernameAccess) {
        throw new Error(
          'Access denied: You do not have permission to access this application. Required roles: admin, data-admin, finance, or finance-advisor'
        )
      }

      const tokens: AuthTokens = {
        accessToken: session.tokens.accessToken.toString(),
        idToken: session.tokens.idToken.toString(),
      }

      // Store tokens based on remember me setting (admin portal specific)
      if (rememberMe) {
        // Remember me: Store in localStorage (persists across browser sessions)
        localStorage.setItem('admin-auth-tokens', JSON.stringify(tokens))
        localStorage.setItem('admin-auth-user', JSON.stringify(cognitoUser))
        localStorage.setItem('admin-remember-me', 'true')
        // Clear sessionStorage versions
        sessionStorage.removeItem('admin-auth-tokens')
        sessionStorage.removeItem('admin-auth-user')
      } else {
        // Don't remember: Store in sessionStorage (expires when browser closes)
        sessionStorage.setItem('admin-auth-tokens', JSON.stringify(tokens))
        sessionStorage.setItem('admin-auth-user', JSON.stringify(cognitoUser))
        localStorage.removeItem('admin-remember-me')
        // Clear localStorage versions
        localStorage.removeItem('admin-auth-tokens')
        localStorage.removeItem('admin-auth-user')
      }

      // Mark this as an admin portal session
      sessionStorage.setItem(ADMIN_PORTAL_ID, 'active')
      sessionStorage.setItem('admin-auth-initialized', Date.now().toString())
      PortalIsolation.markPortalActive('admin')

      // Clear any finance portal sessions to prevent cross-portal access
      localStorage.removeItem('finance-auth-tokens')
      localStorage.removeItem('finance-auth-user')
      sessionStorage.removeItem('finance-auth-initialized')

      return { user: cognitoUser, tokens }
    } catch (error: any) {
      console.error('Sign in error:', error)

      // Preserve the original error structure for better error handling
      if (error.__type || error.name) {
        // This is likely an AWS Cognito error, preserve its structure
        throw error
      } else {
        // For other errors, wrap in a generic error
        throw new Error(error.message || 'Sign in failed')
      }
    }
  }

  /**
   * Sign out the current user
   */
  static async signOut(): Promise<void> {
    try {
      await signOut()
      // Clear all auth-related items from both storages
      this.clearAuthState()
    } catch (error: any) {
      console.error('Sign out error:', error)
      // Clear all storage even if signOut fails
      this.clearAuthState()
      throw new Error(error.message || 'Sign out failed')
    }
  }

  /**
   * Request password reset - sends verification code to user's email
   */
  static async requestPasswordReset(username: string): Promise<void> {
    try {
      await resetPassword({ username })
    } catch (error: any) {
      console.error('Request password reset error:', error)

      // Handle specific Cognito errors
      if (error.__type || error.name) {
        throw error
      } else {
        throw new Error(error.message || 'Failed to send password reset code')
      }
    }
  }

  /**
   * Confirm password reset with verification code and new password
   */
  static async confirmPasswordReset(
    username: string,
    confirmationCode: string,
    newPassword: string
  ): Promise<void> {
    try {
      await confirmResetPassword({
        username,
        confirmationCode,
        newPassword,
      })
    } catch (error: any) {
      console.error('Confirm password reset error:', error)

      // Handle specific Cognito errors
      if (error.__type || error.name) {
        throw error
      } else {
        throw new Error(error.message || 'Failed to reset password')
      }
    }
  }

  /**
   * Check if this is a valid admin portal session
   */
  static isValidAdminSession(): boolean {
    // Use portal isolation check
    if (!PortalIsolation.isPortalActive('admin')) {
      return false
    }

    // Check if this tab has an active admin session
    const adminSession = sessionStorage.getItem(ADMIN_PORTAL_ID)
    if (adminSession !== 'active') {
      return false
    }

    // Check if there are finance tokens present (indicating finance portal login)
    const financeTokens = localStorage.getItem('finance-auth-tokens')
    const financeUser = localStorage.getItem('finance-auth-user')

    if (financeTokens || financeUser) {
      // Finance session exists, this admin session is invalid
      this.clearAuthState()
      return false
    }

    return true
  }

  /**
   * Get current user session
   */
  static async getCurrentSession(): Promise<{
    user: CognitoUser
    tokens: AuthTokens
  } | null> {
    try {
      // First check if this is a valid admin session
      if (!this.isValidAdminSession()) {
        return null
      }

      const [user, session] = await Promise.all([
        getCurrentUser(),
        fetchAuthSession(),
      ])

      if (!session.tokens?.accessToken || !session.tokens?.idToken) {
        return null
      }

      // Decode the ID token to get user information
      const payload = jwtDecode(session.tokens.idToken.toString()) as any

      const cognitoUser: CognitoUser = {
        id: payload.sub,
        username: user.username,
        email: payload.email,
        email_verified: payload.email_verified,
        given_name: payload.given_name,
        family_name: payload.family_name,
        name: payload.name,
        groups: payload['cognito:groups'] || [],
      }

      const tokens: AuthTokens = {
        accessToken: session.tokens.accessToken.toString(),
        idToken: session.tokens.idToken.toString(),
      }

      // Update stored tokens in the correct location (admin portal specific)
      const isRemembered = localStorage.getItem('admin-remember-me') === 'true'
      if (isRemembered) {
        localStorage.setItem('admin-auth-tokens', JSON.stringify(tokens))
        localStorage.setItem('admin-auth-user', JSON.stringify(cognitoUser))
      } else {
        sessionStorage.setItem('admin-auth-tokens', JSON.stringify(tokens))
        sessionStorage.setItem('admin-auth-user', JSON.stringify(cognitoUser))
      }

      return { user: cognitoUser, tokens }
    } catch (error) {
      console.error('Get current session error:', error)
      // Clear invalid tokens (admin portal specific)
      localStorage.removeItem('admin-auth-tokens')
      localStorage.removeItem('admin-auth-user')
      return null
    }
  }

  /**
   * Refresh the current session
   */
  static async refreshSession(): Promise<AuthTokens | null> {
    try {
      const session = await fetchAuthSession({ forceRefresh: true })

      if (!session.tokens?.accessToken || !session.tokens?.idToken) {
        return null
      }

      const tokens: AuthTokens = {
        accessToken: session.tokens.accessToken.toString(),
        idToken: session.tokens.idToken.toString(),
      }

      // Store tokens in the correct location based on remember me setting
      const isRemembered = localStorage.getItem('admin-remember-me') === 'true'
      if (isRemembered) {
        localStorage.setItem('admin-auth-tokens', JSON.stringify(tokens))
      } else {
        sessionStorage.setItem('admin-auth-tokens', JSON.stringify(tokens))
      }

      return tokens
    } catch (error) {
      console.error('Refresh session error:', error)
      return null
    }
  }

  /**
   * Get stored tokens from localStorage or sessionStorage
   */
  static getStoredTokens(): AuthTokens | null {
    try {
      // Try localStorage first (remember me = true)
      let tokensStr = localStorage.getItem('admin-auth-tokens')
      if (tokensStr) return JSON.parse(tokensStr)

      // Then try sessionStorage (remember me = false)
      tokensStr = sessionStorage.getItem('admin-auth-tokens')
      if (tokensStr) return JSON.parse(tokensStr)

      return null
    } catch (error) {
      console.error('Error getting stored tokens:', error)
      return null
    }
  }

  /**
   * Get stored user from localStorage or sessionStorage
   */
  static getStoredUser(): CognitoUser | null {
    try {
      // Try localStorage first (remember me = true)
      let userStr = localStorage.getItem('admin-auth-user')
      if (userStr) return JSON.parse(userStr)

      // Then try sessionStorage (remember me = false)
      userStr = sessionStorage.getItem('admin-auth-user')
      if (userStr) return JSON.parse(userStr)

      return null
    } catch (error) {
      console.error('Error getting stored user:', error)
      return null
    }
  }

  /**
   * Clear all authentication state
   */
  static clearAuthState(): void {
    // Clear localStorage
    localStorage.removeItem('admin-auth-tokens')
    localStorage.removeItem('admin-auth-user')
    localStorage.removeItem('admin-remember-me')
    localStorage.removeItem('finance-auth-tokens')
    localStorage.removeItem('finance-auth-user')
    localStorage.removeItem('auth-store')

    // Clear sessionStorage
    sessionStorage.removeItem('admin-auth-tokens')
    sessionStorage.removeItem('admin-auth-user')
    sessionStorage.removeItem('admin-auth-initialized')
    sessionStorage.removeItem('auth-initialized')
  }

  /**
   * Check if user is authenticated
   */
  static isAuthenticated(): boolean {
    const tokens = this.getStoredTokens()
    if (!tokens) return false

    try {
      // Check if access token is not expired
      const decodedToken = jwtDecode(tokens.accessToken) as any
      const currentTime = Date.now() / 1000
      return decodedToken.exp > currentTime
    } catch (error) {
      console.error('Error checking authentication:', error)
      return false
    }
  }

  /**
   * Validate token and refresh if expired
   * Returns valid token or null if refresh fails
   */
  static async validateAndRefreshToken(): Promise<string | null> {
    const tokens = this.getStoredTokens()
    if (!tokens) {
      return null
    }

    try {
      // Check if access token is expired
      const decodedToken = jwtDecode(tokens.accessToken) as any
      const currentTime = Date.now() / 1000
      const bufferTime = 60 // Refresh if token expires in less than 60 seconds

      if (decodedToken.exp > currentTime + bufferTime) {
        // Token is still valid
        return tokens.accessToken
      }

      // Token is expired or about to expire, try to refresh
      console.log('Token expired or expiring soon, attempting refresh...')
      const newTokens = await this.refreshSession()
      if (newTokens?.accessToken) {
        return newTokens.accessToken
      }

      // Refresh failed
      console.error('Token refresh failed')
      return null
    } catch (error) {
      console.error('Error validating token:', error)
      return null
    }
  }
}
